import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from '@react-router';

import type { LayoutBaseProps } from '../layout.types';
import { Box, Flex, NoSsr } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';

import { FrameWrapper } from './components/FrameWrapper';
import { MainFrame } from './components/MainFrame';
import { AuxFrameProvider } from './components/AuxFrame';
import { Body } from './components/Body';
import { alohaConfig, useAlohaDesign } from './LayoutAloha.theme';
import { WelcomeCard } from './components/WelcomeCard';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import { Footer } from '@apps/guest/packages/layout-engine/components/Footer';
import { alohaFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { PageContainer, CustomPageContainer, StyledFlexAloha } from './LayoutAloha.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { useLayoutAlohaController } from './LayoutAloha.controller';
import { useAlohaScroll } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/useAlohaScroll';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { PreloadImages } from '@apps/guest/packages/layout-engine/components/PreloadPageImages/PreloadImages';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { WebsiteHeader } from './components/WebsiteHeader/WebsiteHeader';
import type { PreparedPages } from './components/Body/Body.constants';
import { pageWidgets } from './components/Body/Body.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFilterMigratedLegacyPages } from '../../hooks/useFilterMigratedLegacyPages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { getMigratedPageSlug, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import queryString from 'query-string';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { filterRsvpPageIfEffectivelyDisabled } from '@apps/guest/packages/layout-engine/common/utils/legacyRsvpQuestions';
import { getCustomArtMobileUrl, getCustomArtUrl, getEventMenuPropsFromEvent, getIsCustomArtEnabled } from '../layout.utils';
import { addRendition } from '@shared/utils/photoRendition';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useTranslation } from '@shared/core';
import { CustomArt } from './components/CustomArt/CustomArt';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import FooterNav from '../../components/FooterNav/FooterNav';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';
import { ALOHA_SCROLL_CONTAINER_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { MobileTopBarNav, MobileTopBarNavOffsetProvider } from '@apps/guest/packages/layout-engine/components/MobileTopBarNav';
import { TopBarNavEventMenu } from '@apps/guest/packages/layout-engine/components/TopBarNavEventMenu';

export interface LayoutAlohaProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  setIsLastPageVisible?: (value: boolean) => void;
  setIsPastFirstPage?: (value: boolean) => void;
}

const { simple } = alohaConfig.applicatorHtmlProps;

const LayoutAloha: React.FC<LayoutAlohaProps> = ({ eventHandle, event, setLoadingTheme, setIsLastPageVisible, setIsPastFirstPage, hideNav, ...restProps }) => {
  const {
    id,
    pages,
    photo,
    mobilePhoto,
    video,
    info: { eventDisplayName, greeting },
  } = event;
  const { eventInfo } = useEventInfo();

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });

  // Filter out legacy pages that have been migrated to custom pages
  const { filteredPages: pagesWithoutMigratedLegacy } = useFilterMigratedLegacyPages(pages);
  // Also drop the RSVP page when no legacy RSVP questions are enabled, so navigation surfaces hide it
  const filteredPages = useMemo(
    () => filterRsvpPageIfEffectivelyDisabled(pagesWithoutMigratedLegacy, event.hasEnabledLegacyRsvpQuestions),
    [pagesWithoutMigratedLegacy, event.hasEnabledLegacyRsvpQuestions],
  );
  const { eventDesign } = event;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isCustomArtEnabled, customArtUrl } = useMemo(
    () => ({
      isCustomArtEnabled: getIsCustomArtEnabled(eventDesign, isMobileOrTablet),
      customArtUrl: isMobileOrTablet ? addRendition({ url: getCustomArtMobileUrl(eventDesign), renditionSize: 'medium' }) : getCustomArtUrl(eventDesign, DesignLayoutType.aloha),
    }),
    [eventDesign, isMobileOrTablet],
  );

  const { isSmallScreen, loading, CustomStyle } = useAlohaDesign(eventHandle, event.eventDesign);
  // Fetch header settings for preload URL partitioning. The query is shared with the WebsiteHeader
  // component (Apollo deduplicates by query+variables), so this adds no extra network request.
  const { websiteHeader: alohaWebsiteHeader } = useWebsiteHeaderSettings({ targetWebsiteLayout: 'aloha', eventHandle });
  // useMediaQuery returns false during SSR (no window.matchMedia).
  // For mobile SSR, assume portrait orientation — most mobile devices are portrait on initial load.
  // The client will correct this on hydration if the device is actually landscape.
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const useMobilePhoto = isMobile ? withWindow(() => isPortrait && isMobile, true) : false;
  const homeRef = useRef<HTMLDivElement>(null);
  const isPreviewing = isInIframe();
  const containerRef = useRef(null);
  // Must be referentially stable so useAlohaScroll's effect doesn't re-run
  // on every render. Brannan's LayoutBrannanSinglePage does this correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;
  const { pageColor, pageFontColor, artBackgroundColor, artBackgroundFontColor } = useColorPaletteProvider();
  const { checkIsWelcomePage, isWelcomePageAsCustomPageEnabled } = useWelcomePage();
  const scrollPages = useMemo(() => {
    if (!hideNav) return filteredPages;
    return filteredPages.filter(page => !checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
  }, [filteredPages, hideNav, checkIsWelcomePage]);

  const hasWelcomePage = useMemo(() => filteredPages.some(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage), [filteredPages, checkIsWelcomePage]);

  useAlohaScroll(scrollPages, pagesRef, containerRef.current, setIsLastPageVisible, setIsPastFirstPage);

  const {
    handleMenuClicked,
    handleWelcomeRSVPButtonClicked,
    handleWelcomeViewDetailsButtonClicked,
    handleFooterButtonClicked,
    handleMenuOpenedChange,
    handleWelcomeOnNotYouButtonClicked,
    handleWelcomeOnNotYouErrorRequest,
    telemetryEditPhotoActions,
  } = useLayoutAlohaController({ eventHandle, pages: filteredPages });

  // Create eventProps with filtered pages for navigation menus
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { tq } = useTranslation('joykit');

  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  // CORE-6488: Legacy slug redirect for Aloha layout.
  // Same logic as LayoutBrannan — when a URL has a pre-migration slug (e.g. /event/weddingparty),
  // it won't match any page in filteredPages (filtered out by useFilterMigratedLegacyPages).
  // Redirect to the canonical URL so the scroll-to-page logic finds the right section.
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  const history = useHistory();
  const queryParams = useQueryParams();
  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  useEffect(() => {
    if (!currentPageSlug) return;

    // Check if the slug resolves in the filtered pages
    const matchesFiltered = filteredPages.some(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (matchesFiltered) return;

    // Slug doesn't match filtered pages — check the unfiltered list for a legacy page
    const legacyPage = pages.find(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (!legacyPage) return;

    const canonicalSlug = getMigratedPageSlug(legacyPage.type) ?? toCanonicalMigratedSlug(legacyPage.pageSlug);
    if (!canonicalSlug || canonicalSlug === currentPageSlug) return;

    const livePage = filteredPages.find(p => p.pageSlug === canonicalSlug || toCanonicalMigratedSlug(p.pageSlug) === canonicalSlug);
    const canonicalPath = livePage ? eventPageRoute.goToPath(livePage.type, livePage.pageSlug) : `/${eventHandle}/${canonicalSlug}`;

    if (canonicalPath && !canonicalPath.endsWith(`/${currentPageSlug}`)) {
      history.replace(
        queryString.stringifyUrl({
          url: canonicalPath,
          query: queryParams,
        }),
      );
    }
  }, [currentPageSlug, pages, filteredPages, eventHandle, eventPageRoute, history, queryParams]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, event.eventDesign?.theme.websiteLayoutAlohaSettings?.alohaColorPreference?.supportsCustomization]);

  const onEditThemeColorClick = useCallback(() => {
    // send the message to the parent window to open the theme editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editThemeColor',
        inlineEditData: {
          imageURL: filteredPages.find(p => checkIsWelcomePage(p.pageSlug, p.type).isWelcomePage)?.photoV2?.url || '',
          eventDesignId: event.eventDesign?.id || '',
        },
      },
    });
  }, [event.eventDesign?.id, filteredPages, checkIsWelcomePage]);

  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: alohaWebsiteHeader?.headerPhoto });

  const indexOfValidPageWidgetAfterWelcomePage = useMemo(() => {
    const indexOfWelcome = filteredPages.findIndex(p => {
      const { isWelcomePage } = checkIsWelcomePage(p.pageSlug, p.type);
      return isWelcomePage;
    });
    // if welcome page is not found, then we don't need to disable the top margin
    if (indexOfWelcome === -1) {
      return -1;
    }
    const indexOfFirstValidPageAfterWelcome = filteredPages.slice(indexOfWelcome + 1).findIndex(p => pageWidgets[p.type as PreparedPages]);

    // Return -1 if no valid page found, otherwise calculate the actual index
    return indexOfFirstValidPageAfterWelcome === -1 ? -1 : indexOfFirstValidPageAfterWelcome + indexOfWelcome + 1;
  }, [filteredPages, checkIsWelcomePage]);

  return (
    <MobileTopBarNavOffsetProvider onMenuOpenChange={handleMenuOpenedChange}>
      <FrameWrapper>
        {CustomStyle && <CustomStyle />}
        <PreloadImages
          pages={filteredPages}
          eventHandle={eventHandle}
          headerPhoto={alohaWebsiteHeader?.headerPhoto}
          fallbackPhotoUrl={photo?.url}
        />
        <AuxFrameProvider
          isSmallScreen={isSmallScreen}
          eventId={id}
          primaryPhoto={useMobilePhoto ? mobilePhoto : photo}
          pages={filteredPages}
          isPreviewing={isPreviewing}
        >
          <StyledFlexAloha
            ref={containerRef}
            loading={loading}
            isPreviewing={isPreviewing}
            className="aloha-scrollable"
            id={ALOHA_SCROLL_CONTAINER_ID}
            heightOffset={bannerOffset}
          >
            <div
              id="photo-welcome"
              ref={homeRef}
            />
            {!isMobile && !hideNav && (
              <WebsiteHeader
                eventMenuProps={eventMenuProps}
                artBackgroundFontColor={artBackgroundFontColor}
                telemetryEditPhotoActions={telemetryEditPhotoActions}
                handleMenuOpenedChange={handleMenuOpenedChange}
                handleMenuClicked={handleMenuClicked}
                eventDisplayName={eventDisplayName}
                greeting={greeting}
              />
            )}
            {/* The mobile menu normally renders inside the welcome page header. */}
            {isMobile && !hideNav && !hasWelcomePage && (
              <TopBarNavEventMenu
                eventProps={eventMenuProps}
                handleMenuOpenedChange={handleMenuOpenedChange}
                handleMenuClicked={handleMenuClicked}
                title={eventDisplayName}
                eventHandle={eventHandle}
                backgroundColor={pageColor}
              />
            )}
            {!hideNav && (
              <MobileTopBarNav
                eventHandle={eventHandle}
                pages={eventMenuProps.pages}
              />
            )}
            <MainFrame backgroundColor={pageColor}>
              {filteredPages.map((page, idx) => {
                const { isWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);

                if (hideNav && isWelcomePage) {
                  return null;
                }

                if (isWelcomePage) {
                  const isCustomVariant = isWelcomePageAsCustomPageEnabled && page.type === EventPageType.custom;

                  return (
                    <PageContainer
                      id={page.pageSlug}
                      backgroundColor={artBackgroundColor}
                      textColor={artBackgroundFontColor}
                      key={page.id}
                      ref={ref => (pagesRef[page.id] = { ref, page })}
                      disableBottomMargin
                    >
                      <div>
                        {isMobile && !hideNav && (
                          <WebsiteHeader
                            eventMenuProps={eventMenuProps}
                            artBackgroundFontColor={artBackgroundFontColor}
                            telemetryEditPhotoActions={telemetryEditPhotoActions}
                            handleMenuOpenedChange={handleMenuOpenedChange}
                            handleMenuClicked={handleMenuClicked}
                            eventDisplayName={eventDisplayName}
                            greeting={greeting}
                          />
                        )}
                        {isCustomVariant ? (
                          <Box position="relative">
                            {/* Art layer: clips the bottom-anchored art (CustomArt / `simple` sprite) to the
                                welcome page so it can't bleed into the next page. Kept in its own absolute
                                layer — not on the outer wrapper — so Body's page InlineEditor label/toolbar
                                (positioned above the page at negative offsets) aren't clipped.
                                `overflow: hidden` (not `clip`) for iOS <16 / older-Android webview support. */}
                            <Box
                              position="absolute"
                              top={0}
                              right={0}
                              bottom={0}
                              left={0}
                              overflow="hidden"
                              pointerEvents="none"
                              // Keep the art layer behind the content: absolute siblings otherwise
                              // paint over the non-positioned Body, and `simple.prop` is opaque.
                              zIndex={0}
                              {...(!isCustomArtEnabled ? simple.prop : {})}
                            >
                              {isCustomArtEnabled && <CustomArt backgroundImageUrl={customArtUrl} />}
                            </Box>
                            <Box
                              position="relative"
                              zIndex={1}
                            >
                              <Body
                                key={page.id}
                                data-testid={`layout-body-${page.pageSlug}`}
                                page={page}
                                event={event}
                                eventHandle={eventHandle}
                                pageTitle={page.pageTitle}
                                isWelcomePage={true}
                                position="relative"
                              />
                            </Box>
                          </Box>
                        ) : (
                          <InlineEditor
                            elementLabel={tq`Theme`}
                            wrapperType="actionInside"
                            actionData={{
                              editThemeColor: onEditThemeColorClick,
                            }}
                            componentName="welcomePageTheme"
                            pageName={page.type}
                            pageSlug={page.pageSlug}
                            wrapperCSS={{ zIndex: 'docked' }}
                            // No longer preventing user from choosing an art background color
                            isEligibleForInlineEditing={true}
                          >
                            <WelcomeCard
                              homeRef={homeRef}
                              page={page}
                              eventHandle={eventHandle}
                              hideEventCountdown={eventInfo?.settings.hideEventCountdown ?? false}
                              finalizedDate={event.info.finalizedDate}
                              location={event.info.location}
                              pages={filteredPages}
                              onRSVPButtonClicked={handleWelcomeRSVPButtonClicked}
                              onViewDetailsButtonClicked={handleWelcomeViewDetailsButtonClicked}
                              onNotYouButtonClicked={handleWelcomeOnNotYouButtonClicked}
                              onNotYouErrorRequest={handleWelcomeOnNotYouErrorRequest}
                              isCustomArtEnabled={isCustomArtEnabled}
                              customArtUrl={customArtUrl}
                              {...restProps}
                            />
                          </InlineEditor>
                        )}
                        {video ? (
                          <LazyWelcomeVideo
                            key="welcome-video"
                            video={video}
                            thumbnailUrl={video.thumbnailUrl || welcomePhotoUrl}
                          />
                        ) : null}
                      </div>
                    </PageContainer>
                  );
                }

                // Non-welcome pages are below the fold — defer to client rendering
                if (page.type === 'custom' && page?.pageSlug === 'accommodations') {
                  return (
                    <CustomPageContainer
                      key={page.id}
                      id={page.pageSlug}
                      backgroundColor={pageColor}
                      textColor={pageFontColor}
                      ref={ref => (pagesRef[page.id] = { ref, page })}
                      disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                    >
                      <Body
                        data-testid={`layout-body-${page.pageSlug}`}
                        page={page}
                        event={event}
                        eventHandle={eventHandle}
                        applicatorProps={simple.prop || {}}
                        pageTitle={page.pageTitle}
                      />
                    </CustomPageContainer>
                  );
                }

                if (!pageWidgets[page.type as PreparedPages]) {
                  return null;
                }

                return (
                  <PageContainer
                    key={page.id}
                    id={page.type}
                    backgroundColor={pageColor}
                    textColor={pageFontColor}
                    ref={ref => (pagesRef[page.id] = { ref, page })}
                    disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                  >
                    <Body
                      data-testid={`layout-body-${page.pageSlug}`}
                      page={page}
                      event={event}
                      eventHandle={eventHandle}
                      applicatorProps={simple.prop || {}}
                      pageTitle={page.pageTitle}
                    />
                  </PageContainer>
                );
              })}
              <NoSsr>
                <Flex __css={alohaFooterContainerStyles}>
                  {/* FooterNav is only visible on mobile */}
                  {isGuestMobileFooterNavEnabled && !hideNav && (
                    <Flex
                      width="100%"
                      display={{ _: 'flex', sm2: 'none' }}
                    >
                      <FooterNav
                        pages={filteredPages}
                        eventHandle={eventHandle}
                        eventDisplayName={eventDisplayName}
                        handleMenuClicked={handleMenuClicked}
                      />
                    </Flex>
                  )}
                  <Footer
                    onButtonClicked={handleFooterButtonClicked}
                    data-testid={'layout-footer'}
                  />
                </Flex>
              </NoSsr>
            </MainFrame>
          </StyledFlexAloha>
        </AuxFrameProvider>
      </FrameWrapper>
    </MobileTopBarNavOffsetProvider>
  );
};

LayoutAloha.displayName = 'LayoutAloha';

export { LayoutAloha };
